import _ from 'lodash';
import dayjs from 'dayjs';
import { PaymentTypeCodes } from '../../../constants';
import generateGeneralIntroMessage from './generalIntroMessage';
import currencySymbol from '../../../helpers/currencySymbol';

/**
 * Function returns high-level hotel data
 */
const getHighLevelHotelData = (itinerary, currencySign) => {
  const data = `
    Country: ${itinerary.hotel.country}
    State: ${itinerary.hotel.geoname && itinerary.hotel.geoname.stateOrCountry}
    City: ${itinerary.hotel.city}
    Address: ${itinerary.hotel.address}
    Latitude: ${itinerary.hotel.lat}
    Longitude: ${itinerary.hotel.lon}
    Airport code: ${itinerary.hotel.airportCode
    || (itinerary.hotel.geoname ? itinerary.hotel.geoname.cityCode : null)}
    zip code: ${itinerary.hotel.zipCode}
    Hotel name: ${itinerary.hotel.hotelName || itinerary.hotel.name}
    Hotel chain: ${itinerary.hotel.hotelChainName}
    Has free airport shuttle: ${itinerary.hotel.hasFreeAirportShuttle}
    Hotel Amenities: ${(itinerary.hotel.amenities || []).join(', ')}
    Base room price per night: ${currencySign}${itinerary.hotel.price ? itinerary.hotel.price.baseRate : ''}
  `;
  return data;
};

/**
 * Function returns detailed hotel data
 */
const getDetailedHotelData = (hotel, currencySign) => {
  // Get detailed data
  let data = `Hotel name is ${hotel.name}. `;
  data += `The hotel's address is ${hotel.fullAddress}. `;
  const checkIn = dayjs(hotel.checkInStartTime).format('MMM D');
  const checkOut = dayjs(hotel.checkOutEndTime).format('MMM D');
  data += `The user wants to stay there from ${checkIn} to ${checkOut}. `;
  data += 'Assume the user\'s queries are about these dates. ';
  data += 'All prices below are with respect to these dates. ';
  // Amenities
  data += 'The amenities are: ';
  _.each(hotel.amenities, (amenity) => {
    data += `${amenity}, `;
  });
  // Shuttle
  if (hotel.hasFreeAirportShuttle) {
    data += 'The hotel offers a free airport shuttle. ';
  } else {
    data += 'The hotel does not offer a free airport shuttle. ';
  }
  data += `The hotel's rating is ${hotel.starRating} stars. `;
  data += `The hotel chain is ${hotel.hotelChainName}. `;

  let roomSummary = '';
  roomSummary += 'The hotel offers the following rooms:\n';
  _.each(hotel.products, (product) => {
    _.each(product.rooms, (room) => {
      roomSummary += '- ';
      roomSummary += `${room.category}. `;
      roomSummary += `${currencySign}${product.roomPrice.averageNightlyRate} per night. `;
      roomSummary += (product.isPrepaidRate ? 'Prepaid. ' : '');
      roomSummary += (product.roomPrice.isCommissionable ? 'Commissionable. ' : 'Not commissionable. ');
      roomSummary += (product.nonRefundable ? 'Not refundable. ' : 'Refundable. ');
      switch (product.paymentTypeCode) {
        case PaymentTypeCodes.DEPOSIT:
          roomSummary += 'Deposit required. ';
          break;
        case PaymentTypeCodes.GUARANTEE:
          roomSummary += 'Guarantee required. ';
          break;
        case PaymentTypeCodes.LATE:
          roomSummary += 'Guarantee late arrival. ';
          break;
        default:
          break;
      }
      _.each(product.valueAdds, (value) => {
        roomSummary += `${value}. `;
      });
      _.each(product.amenities, (amenity) => {
        roomSummary += `${amenity}. `;
      });
      roomSummary += '\n';
    });
  });
  data += roomSummary;

  const generalInfo = hotel && hotel.info && hotel.info
    .replace(/<br\s*\/>/g, '\n') // Replace all <br/> tags with newlines
    .replace(/<\/?[\w\s"'=:;\-\\]*\/?>/g, ' '); // Delete all HTML tags (ex: <div> or </div> or <br />)
  data += generalInfo ? `Here is more info about the hotel:\n${generalInfo}` : '';

  return data;
};

// --------------------------------------------------------------- //
// Section below are hotel's instructions generating functions
// --------------------------------------------------------------- //

/**
 * A fast generated introductory message for hotel before the time-consuming
 * data fetching are completed
 */
const generateHotelGeneralIntroMessage = (itinerary, nlpConstants, nlpInstructions) => {
  // Get the top-level general intro message
  const generalIntro = generateGeneralIntroMessage(nlpConstants, nlpInstructions);

  // Handle potential error
  if (!itinerary || !itinerary.hotel) {
    return generalIntro;
  }

  // Get the currency sign
  let currencySign = '';
  if (itinerary.cost && itinerary.cost.currencyCode) {
    currencySign = currencySymbol(itinerary.cost.currencyCode);
  }

  // Get high-level hotel data
  const highLevelHotelData = getHighLevelHotelData(itinerary, currencySign);

  return highLevelHotelData;
};

/**
 * The intro message for hotel when no detailed data. We will give NLP model the
 * highlevel data and tell it the fact that we don't have detailed data after fetching.
 */
const generateHotelNoDetailedDataIntroMessage = (
  itinerary,
  currencySign,
) => {
  // Get high-level hotel data
  const highLevelHotelData = getHighLevelHotelData(itinerary, currencySign);
  return highLevelHotelData;
};

/**
 * Function generates introductory message for hotel
 */
const generateHotelIntroMessage = (hotelDetails, itinerary) => {
  // Get the currency sign
  let currencySign = '';
  if (hotelDetails.cost && hotelDetails.cost.currencyCode) {
    currencySign = currencySymbol(hotelDetails.cost.currencyCode);
  }

  // When data fetching done, no error, but no hotel details data either
  if (!hotelDetails || !hotelDetails.hotel) {
    return generateHotelNoDetailedDataIntroMessage(
      itinerary,
      currencySign,
    );
  }

  // Get raw data
  const { hotel } = hotelDetails;

  // Get detailed hotel data
  const detailedHotelData = getDetailedHotelData(hotel, currencySign);

  return detailedHotelData;
};

export { generateHotelIntroMessage, generateHotelGeneralIntroMessage, getDetailedHotelData };
